import { Directive, HostListener, Injectable } from '@angular/core';
import { NavManagerService } from '../services/nav-manager.service';
import { DpadZoneDirective } from './dpad-zone.directive';
import { DpadSelectableDirective } from './dpad-selectable.directive';


@Directive({
    selector: '[dpadContainer]'
})
@Injectable({
    providedIn: 'root'
})
export class DpadContainerDirective {

    private filterMargin: number = 20;

    dpadZones: DpadZoneDirective[] = [];
    _selectedZone: DpadZoneDirective;
    get selectedZone(): DpadZoneDirective {
        return this._selectedZone;
    }

    set selectedZone(val: DpadZoneDirective) {
        this._selectedZone = val;
        this.lastSelected = val;
    }

    lastSelected: DpadZoneDirective;

    constructor(private navManagerService: NavManagerService) {

        this.navManagerService.addZoneBroker.subscribe(zoneEvent => {
            this.dpadZones.push(zoneEvent);


            let current = zoneEvent.dpadSelectables.find((f: DpadSelectableDirective) => f.start());

            if (current) {
                this.selectedZone = zoneEvent;
            }
        });

        this.navManagerService.removeZoneBroker.subscribe(zoneName => {
            this.removeZone(zoneName);
        });

        this.navManagerService.moveZoneBroker.subscribe(zoneName => {
            this.setupZone(zoneName);
        });
    }

    removeZone(zoneName: string) {
        let index = this.dpadZones.findIndex(d => d.dpadZone === zoneName);
        this.dpadZones.splice(index, 1);
    }

    setupZone(zoneName: string) {
        this.selectedZone = this.dpadZones.find(f => f.dpadZone === zoneName);
    }

    goToLastSelectedZone() {
        this._selectedZone = this.lastSelected;
        if (this.lastSelected) {
            this._selectedZone.goToZone();
        }
    }

    @HostListener('click')
    onClick() {
        this.goToLastSelectedZone();
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {

        let key = event.key;
        if (key === 'Unidentified') {
            const xboxKeyMap: Record<number, string> = {
                37:  'ArrowLeft',
                205: 'ArrowLeft',

                206:  'ArrowRight',
                39: 'ArrowRight',

                203:  'ArrowUp',
                38: 'ArrowUp',

                204:  'ArrowDown',
                40: 'ArrowDown',

                195: 'EnterXbox',

                27: 'BackspaceXbox'
            };

            key = xboxKeyMap[event.keyCode] || xboxKeyMap[event.which] || key;
        }

        if (!this.selectedZone) {
            this.selectedZone = this.dpadZones.find(f => f.selected());
        }

        let current = this.selectedZone.dpadSelectables.find((f: DpadSelectableDirective) => f.selected())!;

        if (!current) {
            this.dpadZones.forEach(z => {
                let newCurrent = z.dpadSelectables.find((f: DpadSelectableDirective) => f.selected())!;
                if (newCurrent) {
                    current = newCurrent;
                    this.selectedZone = z;
                }
            });
        }
        let currentZone = this.selectedZone;

        if (key === 'ArrowLeft') {
            if (current) {
                let next = this.closestUnderX(current, this.selectedZone.dpadSelectables);
                if (next) {
                    next.focus();
                } else {
                    let next = this.closestUnderX(current, this.dpadZones);
                    if (next) {
                        this.setupZone(next.dpadZone);
                        let zoneSuccess = next.focus();
                        if (!zoneSuccess) {
                            this.selectedZone = currentZone;
                        }
                    }
                }
            }
        } else if (key === 'ArrowRight') {
            if (current) {
                let next = this.closestOverX(current, this.selectedZone.dpadSelectables);
                if (next) {
                    next.focus();
                } else {
                    let next = this.closestOverX(current, this.dpadZones);
                    if (next) {
                        this.setupZone(next.dpadZone);
                        let zoneSuccess = next.focus();
                        if (!zoneSuccess) {
                            this.selectedZone = currentZone;
                        }
                    }
                }
            }
        } else if (key === 'ArrowUp') {
            let next = this.closestUnderY(current, this.selectedZone.dpadSelectables);
            if (next) {
                next.focus();
            } else {
                let next = this.closestUnderY(current, this.dpadZones);
                if (next) {
                    this.setupZone(next.dpadZone);
                    let zoneSuccess = next.focus();
                    if (!zoneSuccess) {
                        this.selectedZone = currentZone;
                    }
                }
            }
        } else if (key === 'ArrowDown') {
            let next = this.closestOverY(current, this.selectedZone.dpadSelectables);
            if (next) {
                next.focus();
            } else {
                let next = this.closestOverY(current, this.dpadZones);
                if (next) {
                    this.setupZone(next.dpadZone);
                    let zoneSuccess = next.focus();
                    if (!zoneSuccess) {
                        this.selectedZone = currentZone;
                    }
                }
            }
        } else if (key === 'EnterXbox') {
            event.preventDefault();

            let elementToClick: HTMLElement | null = null;
            
            if (current && (current as any).elementRef) {
                elementToClick = (current as any).elementRef.nativeElement;
            }
            
            if (!elementToClick && document.activeElement instanceof HTMLElement) {
                elementToClick = document.activeElement;
            }
            
            if (elementToClick) {
                const clickEvent = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window
                });
                elementToClick.dispatchEvent(clickEvent);
            }
        } else if (key === 'BackspaceXbox') {
            event.preventDefault();
            window.history.back();
        }
    }

    private xFilter(selected: DpadSelectableDirective, dpads: DpadSelectableDirective[]) {
        const potentialKeys = dpads.filter(f =>
            f.yEnd() > selected.yMid() - this.filterMargin
            && f.y() < selected.yMid() + this.filterMargin);

        if (potentialKeys.length > 0) {
            return potentialKeys;
        }
        return dpads;
    }

    private yFilter(selected: DpadSelectableDirective, dpads: DpadSelectableDirective[]) {
        const potentialKeys = dpads.filter(f =>
            f.xEnd() > selected.xMid() - this.filterMargin
            && f.x() < selected.xMid() + this.filterMargin);

        if (potentialKeys.length > 0) {
            return potentialKeys;
        }
        return dpads;
    }

    private closestUnderX_V1(valToBeNear: DpadSelectableDirective, dpads: any[]): any {
        let lowestDif = 99999;
        let selectedEl = null;
        dpads = this.xFilter(valToBeNear, dpads);
        dpads.filter(f => f.x() < valToBeNear.x()).forEach(element => {
            if (!element.selected()) {
                let dif = valToBeNear.x() - element.x();
                if (dif < lowestDif) {
                    lowestDif = dif;
                    selectedEl = element;
                }
            }
        });
        return selectedEl;
    }

    private closestOverX_V1(valToBeNear: DpadSelectableDirective, dpads: any[]): any {
        let lowestDif = 99999;
        let selectedEl = null;
        dpads = this.xFilter(valToBeNear, dpads);
        dpads.filter(f => f.x() > valToBeNear.x()).forEach(element => {
            if (!element.selected()) {
                let dif = element.x() - valToBeNear.x();
                if (dif < lowestDif) {
                    lowestDif = dif;
                    selectedEl = element;
                }
            }
        });
        return selectedEl;
    }

    private closestUnderY_V1(valToBeNear: DpadSelectableDirective, dpads: any[]): any {
        let lowestDif = 99999;
        let selectedEl = null;
        dpads = this.yFilter(valToBeNear, dpads);
        dpads.filter(f => f.y() < valToBeNear.y()).forEach(element => {
            if (!element.selected()) {
                let dif = valToBeNear.y() - element.y();
                if (dif < lowestDif) {
                    lowestDif = dif;
                    selectedEl = element;
                }
            }
        });
        return selectedEl;
    }

    private closestOverY_V1(valToBeNear: DpadSelectableDirective, dpads: any[]): any {
        let lowestDif = 99999;
        let selectedEl = null;
        dpads = this.yFilter(valToBeNear, dpads);
        dpads.filter(f => f.y() > valToBeNear.y()).forEach(element => {
            if (!element.selected()) {
                let dif = element.y() - valToBeNear.y();
                if (dif < lowestDif) {
                    lowestDif = dif;
                    selectedEl = element;
                }
            }
        });
        return selectedEl;
    }

    private closestUnderX(valToBeNear: DpadSelectableDirective, dpads: any[]): any {
        let lowestDif = 99999;
        let selectedEl = null;
        dpads = this.xFilter(valToBeNear, dpads);
        dpads.filter(f => f.x() < valToBeNear.x()).forEach(element => {
            if (!element.selected()) {
                let dif = valToBeNear.x() - element.x();
                if (dif < lowestDif) {
                    lowestDif = dif;
                    selectedEl = element;
                }
            }
        });
        return selectedEl;
    }

    private closestOverX(valToBeNear: DpadSelectableDirective, dpads: any[]): any {
        let lowestDif = 99999;
        let selectedEl = null;
        dpads = this.xFilter(valToBeNear, dpads);
        dpads.filter(f => f.x() > valToBeNear.x()).forEach(element => {
            if (!element.selected()) {
                let dif = element.x() - valToBeNear.x();
                if (dif < lowestDif) {
                    lowestDif = dif;
                    selectedEl = element;
                }
            }
        });
        return selectedEl;
    }

    private closestUnderY(valToBeNear: DpadSelectableDirective, dpads: any[]): any {
        let lowestDif = 99999;
        let selectedEl = null;
        dpads = this.yFilter(valToBeNear, dpads);
        dpads.filter(f => f.y() < valToBeNear.y()).forEach(element => {
            if (!element.selected()) {
                let dif = valToBeNear.y() - element.y();
                if (dif < lowestDif) {
                    lowestDif = dif;
                    selectedEl = element;
                }
            }
        });
        return selectedEl;
    }

    private closestOverY(valToBeNear: DpadSelectableDirective, dpads: any[]): any {
        let lowestDif = 99999;
        let selectedEl = null;
        dpads = this.yFilter(valToBeNear, dpads);
        dpads.filter(f => f.y() > valToBeNear.y()).forEach(element => {
            if (!element.selected()) {
                let dif = element.y() - valToBeNear.y();
                if (dif < lowestDif) {
                    lowestDif = dif;
                    selectedEl = element;
                }
            }
        });
        return selectedEl;
    }

}
